import C from "@/modules/dashboard/BillingAccounts.module.css";
import D from "@/modules/dashboard/Dashboard.module.css";

import { Badge } from "@/components/Badge.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WCarousel } from "@/components/carousel/WCarousel.tsx";
import { CreateBillingAccount } from "@/components/forms/CreateBillingAccount.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { AddPaymentMethodButton, TopUpNowButton } from "@/modules/billing/ActionButtons.tsx";
import { billingStateAtom, type EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { type ReactElement, type ReactNode, Suspense } from "react";
import type { Settings } from "react-slick";

interface StatusProps {
    noticeClassName: string;
    itemClassName: string;
    noticeText: ReactNode;
}

// TODO: Move to a shared location and add more shared labels here
function getStatusProps({ status, isOpen, isLimited }: EBillingAccount): StatusProps {
    switch (status) {
        case "ACTIVE":
            return {
                // Frozen accounts use the suspended colors
                itemClassName: isOpen ? C.Clear : C.Suspended,
                noticeClassName: "color-primary",
                noticeText: isLimited ? <Trans>Limited</Trans> : undefined,
            };
        case "PENDING_VALIDATION":
            return {
                itemClassName: C.Clear,
                noticeClassName: "color-primary",
                noticeText: <Trans>Validation pending</Trans>,
            };
        case "SUSPENDED":
            return {
                itemClassName: C.Suspended,
                noticeClassName: "color-error",
                noticeText: <Trans>Suspended</Trans>,
            };
        default:
            exhaustiveSwitchCheck(status);
    }
}

export function BillingAccountItem({ item }: { item: EBillingAccount }) {
    const { t } = useLingui();

    const {
        account: { is_recurring_payment_enabled },
        ongoingAmount,
        unpaidAmount,
        closedReason,
        nextAction,
        isLimited,
        isDefault,
        title,
    } = item;

    const { noticeClassName, noticeText, itemClassName } = getStatusProps(item);
    return (
        <div className={cn(C.BillingAccount, itemClassName)}>
            <div className={C.Header}>
                <div className={cn(C.Notices, noticeClassName)}>
                    {noticeText != null && (
                        <Badge noDot color="white">
                            {noticeText}
                        </Badge>
                    )}
                    {isDefault && (
                        <Badge noDot color="white">
                            <Trans>Default</Trans>
                        </Badge>
                    )}
                </div>

                {is_recurring_payment_enabled && (
                    <div className={C.AutoPay}>
                        <Trans>Automated payment</Trans>
                        <MaskIcon className="jp-icon-checkmark size-0.625rem ml-1" />
                    </div>
                )}

                <div className="font-size-small opacity-50">
                    <Trans>Ongoing balance</Trans>
                </div>
                <div
                    className={cn(
                        "pt-1",
                        // Prevent text from getting too long?
                        // XXX: This is not exactly right since different currencies have different amounts of fractional digits
                        ongoingAmount < 100000 ? "font-size-large" : "font-size-heading",
                    )}
                >
                    <CurrencyBalance value={ongoingAmount} />
                </div>

                {!!unpaidAmount && (
                    <div className="font-size-small pt-1">
                        <Trans>Unpaid</Trans> <CurrencyBalance value={-unpaidAmount} />
                    </div>
                )}
            </div>

            <div className={C.Content}>
                <div className="color-muted font-size-small">
                    <Trans>Account name</Trans>
                </div>
                <div className="font-size-subtitle text-ellipsis">{title}</div>

                <div className={C.Holder}>
                    {closedReason && (
                        <NoticeBlock
                            color={closedReason === "suspended" ? "error" : "primary"}
                            compact
                            icon="jp-warning-icon"
                        >
                            {(() => {
                                switch (closedReason) {
                                    case "pending":
                                        return t`Account details are being validated.`;
                                    case "suspended":
                                        return t`Your account has been suspended`;
                                    case "invoice_verification":
                                    case "missing_payment_method":
                                        return t`Verified payment method is missing`;
                                    case "insufficient_funds":
                                        return t`Insufficient funds`;
                                    default:
                                        exhaustiveSwitchCheck(closedReason);
                                }
                            })()}
                        </NoticeBlock>
                    )}

                    {isLimited && (
                        <NoticeBlock compact icon="jp-warning-icon">
                            <Trans>This billing account is limited</Trans>
                        </NoticeBlock>
                    )}
                </div>

                <div className="text-center pt-4">
                    {(() => {
                        switch (nextAction) {
                            case "wait_for_verification":
                            case "add_payment_method":
                                return <AddPaymentMethodButton variant="ghost" account={item} />;
                            case "top_up":
                            case "must_top_up":
                                return <TopUpNowButton variant="ghost" account={item} />;
                            case "pending":
                            case null:
                            case "contact_support":
                                return (
                                    <WButton
                                        color={nextAction === "contact_support" ? "error" : "primary"}
                                        variant="ghost"
                                        action={billingAccountLink(item)}
                                    >
                                        <Trans>View details</Trans>
                                    </WButton>
                                );
                            default:
                                exhaustiveSwitchCheck(nextAction);
                        }
                    })()}
                </div>
            </div>
        </div>
    );
}

export interface BillingAccountsProps {
    accountItems: ReactElement[];
    accountsIndex?: number;
    createDisabled?: boolean;
}

function BillingAccountsCustom({ accountItems, accountsIndex = -1, createDisabled = true }: BillingAccountsProps) {
    const settings: Settings = {
        dots: true,
        dotsClass: cn(D.Dots, "slick-dots"),
        infinite: true,
        adaptiveHeight: true,
        initialSlide: accountsIndex === -1 ? 0 : accountsIndex,
        customPaging: () => <div className={cn(D.Dot, "dot")} />,
    };

    return (
        <div className={D.Accounts}>
            <div className={cn(D.BlockTitle, "pl-2")}>
                <h2>
                    <Trans>Billing accounts</Trans>
                </h2>

                <WButton
                    className={D.Button}
                    color="primary"
                    variant="ghost"
                    icon="jp-icon-add"
                    action={ResourceCreateLinks.billing_account}
                    isDisabled={createDisabled}
                >
                    <Trans comment="Button: Create new billing account" context="Should be short (1-3 words)">
                        Create
                    </Trans>
                </WButton>
            </div>

            <div className={D.Block}>
                {accountItems?.length > 1 ? (
                    <WCarousel settings={settings} slideWidth={360}>
                        {accountItems}
                    </WCarousel>
                ) : accountItems.length === 1 ? (
                    accountItems
                ) : (
                    <div className={D.Empty}>
                        <MaskIcon className="jp-wallet-icon size-4rem" />
                        <p>
                            <Trans>
                                Without an active billing account you can not create any resources, create one to get
                                started.
                            </Trans>
                        </p>
                        <CreateBillingAccount
                            color="primary"
                            variant="ghost"
                            size="bar"
                            icon="jp-icon-add"
                            isDisabled={createDisabled}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export interface BillingAccountsPropsLoaderProps extends Omit<
    BillingAccountsProps,
    "accountItems" | "accountsIndex" | "canCreate"
> {}

function BillingAccountsLoader({ ...props }: BillingAccountsPropsLoaderProps) {
    const data = useSuspenseQueryAtom(billingStateAtom);

    return (
        <BillingAccountsCustom
            {...props}
            createDisabled={!!data.createDisabledReason}
            accountsIndex={data.defaultIndex}
            accountItems={data.accounts.map((item) => (
                <BillingAccountItem key={item.id} item={item} />
            ))}
        />
    );
}

export function BillingAccounts(props: BillingAccountsPropsLoaderProps) {
    return (
        <Suspense
            fallback={
                <BillingAccountsCustom
                    {...props}
                    accountsIndex={0}
                    accountItems={[<Loading key="loading" delay={0} icon="none" />]}
                />
            }
        >
            <BillingAccountsLoader {...props} />
        </Suspense>
    );
}
